import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 * Only enabled in production mode
 */
export function initSentry() {
  const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize in production with valid DSN
  if (import.meta.env.PROD && SENTRY_DSN) {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Performance Monitoring
      tracesSampleRate: 0.1, // Capture 10% of transactions

      // Session Replay
      replaysSessionSampleRate: 0.1, // Sample 10% of sessions
      replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors

      // Filter out sensitive data
      beforeSend(event) {
        // Remove sensitive headers and data
        if (event.request?.headers) {
          delete event.request.headers['Authorization'];
          delete event.request.headers['Cookie'];
        }
        return event;
      },

      // Ignore specific errors
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',
        // Random plugins/extensions
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
      ],
    });
  } else if (!import.meta.env.PROD) {
    console.info('Sentry is disabled in development mode');
  } else if (!SENTRY_DSN) {
    console.warn('VITE_SENTRY_DSN is not configured');
  }
}

/**
 * Capture an exception manually
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (import.meta.env.PROD) {
    Sentry.captureException(error, { extra: context });
  } else {
    console.error('Error captured:', error, context);
  }
}

/**
 * Capture a message manually
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  if (import.meta.env.PROD) {
    Sentry.captureMessage(message, level);
  } else {
    console.log(`[Sentry ${level}]:`, message);
  }
}

/**
 * Set user context for error tracking
 */
export function setUser(user: { id: string; email?: string } | null) {
  if (import.meta.env.PROD) {
    Sentry.setUser(user);
  }
}
